import { version } from '../../package.json';
import { appState } from '../appState';
import { authService } from '../auth/auth.service';
import { useAuth } from '../auth/useAuth';
import { pushService } from '../push/push.service';
import { Route, useVisibleRoutes } from '../routes';
import serviceVersionsService from '../serviceVersions/serviceVersions.service';
import css from './navigation.module.less';
import { toggleNavigation } from './navigation.store';
import { getNavigationColors } from './navigationColors';
import NavigationGroup from './navigationGroup';
import StoreSelectAutocomplete from './storeSelectAutocomplete';
import { useStores } from '@/lib/hooks/useStores';
import { useTranslator } from '@/translations/translator';
import useInputFocusShortcut from '@/util/useInputFieldFocus';
import { faSignOutAlt, faUser } from '@fortawesome/pro-regular-svg-icons';
import SearchIcon from '@mui/icons-material/Search';
import { Button, InputAdornment, TextField } from '@mui/material';
import List from '@mui/material/List';
import { useBrand } from '@whiz-cart/ui-shared/brand/brandContext.tsx';
import { ClickOutside } from '@whiz-cart/ui-shared/clickOutside/clickOutside.component.tsx';
import { useStableMemo } from '@whiz-cart/ui-shared/hooks/useStable';
import { Img } from '@whiz-cart/ui-shared/img/img.component.tsx';
// @ts-expect-error missing types
import { loadingValue } from '@whiz-cart/ui-shared/loading/loading.jsx';
import { urlService } from '@whiz-cart/ui-shared/url/url.service';
import { groupBy, isEmpty, map } from 'lodash';
import { CSSProperties, useCallback, useDeferredValue, useMemo, useRef, useState } from 'react';
import { textMatch } from 'schummar-table';

export type RouteLike = Pick<Route, 'path' | 'link' | 'icon' | 'brandIcon' | 'label' | 'externalLink' | 'englishLabel'> & {
    exact?: Route['exact'];
    children?: RouteLike[];
    onClick?: () => void;
    isMatchingSearch?: boolean;
    isDirectMatch?: boolean;
};

const routeMatch = (route: RouteLike, query: string): RouteLike => {
    if (!query) {
        return route;
    }

    const children = route.children?.map((child) => routeMatch(child, query));
    const childMatches = children?.some((child) => child.isMatchingSearch);
    const isDirectMatch = textMatch(route.label ?? '', query) || textMatch(route.englishLabel ?? '', query);

    return {
        ...route,
        isMatchingSearch: childMatches || isDirectMatch,
        isDirectMatch,
        children: children?.filter((child) => child.isMatchingSearch),
    };
};

export default function Navigation() {
    const { tokenDetails, getActiveRoles } = useAuth();
    const username = tokenDetails?.unique_name;
    const t = useTranslator();
    const { stores } = useStores();
    const brand = useBrand();
    const navigationColors = useMemo(() => getNavigationColors(brand.palette), [brand.palette]);
    const navigationStyle = useMemo(
        () =>
            ({
                '--navigation-accent': navigationColors.accent,
                '--navigation-surface': navigationColors.surface,
            }) as CSSProperties,
        [navigationColors],
    );
    const hideNavigation = useCallback(() => {
        resetSearch();
        if (document.body.offsetWidth > 599) return;
        toggleNavigation(false);
    }, []);
    const [searchQuery, setSearchQuery] = useState('');
    const storeRowRef = useRef<HTMLDivElement>(null);
    const [focusedSearchResultIndex, setFocusedSearchResultIndex] = useState(0);

    const showVersions = () => {
        serviceVersionsService.show();
    };

    const logout = useCallback(async () => {
        await pushService.unsubscribeAll();
        authService.logout();
    }, []);

    const visibleRoutes = useVisibleRoutes();
    const routes = useMemo(
        () => [
            ...visibleRoutes,
            {
                path: '/account',
                link: '/me',
                icon: faUser,
                label: username,
                group: 'navigation.account',
            },
            {
                path: '/logout',
                link: '/logout',
                onClick: logout,
                icon: faSignOutAlt,
                label: t('navigation.logout'),
                group: 'navigation.account',
            },
        ],
        [visibleRoutes, username, t, logout],
    );

    const searchFieldRef = useRef<HTMLInputElement>(undefined);
    const { modifierText } = useInputFocusShortcut('k', searchFieldRef.current, {
        onOtherKeyPress: handleKeyPress,
        onFocus: () => {
            setFocusedSearchResultIndex(0);
        },
    });

    const query = useDeferredValue(searchQuery);

    const filteredRoutes = useStableMemo(
        () => routes.map((route) => routeMatch(route, query)).filter((route) => !query || route.isMatchingSearch),
        [routes, query],
    );
    const focusableFlatRoutes = useMemo(
        () =>
            filteredRoutes
                .flatMap((route) => (route?.children ? [route, ...route.children] : [route]))
                .filter((route) => route.isDirectMatch),
        [filteredRoutes],
    );

    const groups = useMemo(() => groupBy(filteredRoutes, 'group'), [filteredRoutes]);
    const activeRoles = getActiveRoles();
    const hasNoRoles = Object.entries(activeRoles).length === 0;

    const routesByPath = useMemo(() => {
        const map: Record<string, RouteLike> = {};
        function visit(list: RouteLike[], parent?: RouteLike) {
            for (const route of list) {
                const withInheritedIcon =
                    route.icon || route.brandIcon ? route : { ...route, icon: parent?.icon, brandIcon: parent?.brandIcon };
                map[route.path] = withInheritedIcon;
                if (route.children) visit(route.children, withInheritedIcon);
            }
        }
        visit(routes);
        return map;
    }, [routes]);

    const pinnedItemPaths = appState.useStore((s) => s.pinnedNavigationItems);
    const pinnedRoutes = useMemo(
        () => (pinnedItemPaths ?? []).map((path) => routesByPath[path]).filter((route): route is RouteLike => !!route),
        [pinnedItemPaths, routesByPath],
    );

    function resetSearch() {
        setSearchQuery('');
    }

    function isHighlighted(route: RouteLike) {
        const activeRoute = focusableFlatRoutes[focusedSearchResultIndex];
        return activeRoute && route.link === activeRoute.link;
    }

    function handleKeyPress(e: KeyboardEvent) {
        if (document.activeElement !== searchFieldRef.current) {
            return;
        }
        if (e.key === 'ArrowDown') {
            setFocusedSearchResultIndex((prevIndex) => {
                const nextIndex = prevIndex + 1;
                if (nextIndex >= focusableFlatRoutes.length) {
                    return 0;
                }
                return nextIndex;
            });
        } else if (e.key === 'ArrowUp') {
            setFocusedSearchResultIndex((prevIndex) => {
                const nextIndex = prevIndex - 1;
                if (nextIndex < 0) {
                    return focusableFlatRoutes.length - 1;
                }
                return nextIndex;
            });
        } else if (e.key === 'Enter') {
            const activeRoute = focusableFlatRoutes[focusedSearchResultIndex];
            if (activeRoute) {
                searchFieldRef.current?.blur();
                if (activeRoute.externalLink) {
                    window.open(activeRoute.externalLink, '_blank');
                } else {
                    urlService.pushUrl(activeRoute.link);
                }
                hideNavigation();
                setFocusedSearchResultIndex(0);
            }
        } else if (e.key === 'Escape') {
            searchFieldRef.current?.blur();
            hideNavigation();
            setFocusedSearchResultIndex(0);
        } else {
            return;
        }
        e.preventDefault();
    }

    return (
        <ClickOutside
            data-testid="navigationBarContainer"
            onClickOutside={hideNavigation}
            className={css.navigation}
            style={navigationStyle}
        >
            <div data-testid="navigationStore" className={css.store} ref={storeRowRef}>
                <Img data-testid="logo" className={css.headerLogo} src={{ brandImage: navigationColors.logo }} />
                {loadingValue(<StoreSelectAutocomplete stores={stores} onSelect={resetSearch} popupAnchorRef={storeRowRef} />, {
                    check: !isEmpty(stores),
                    label: !hasNoRoles ? t('navigation.loading') : t('navigation.notAvailable'),
                    className: !hasNoRoles ? css.loading : css.unavailable,
                })}
            </div>

            <List className={css.container}>
                <div data-testid="navigationItems" className={css.items}>
                    <TextField
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                            endAdornment: <div className={css.shortcutAdornment}>{modifierText}</div>,
                        }}
                        inputRef={searchFieldRef}
                        value={searchQuery}
                        onChange={(e) => {
                            setSearchQuery(e.target.value);
                            setFocusedSearchResultIndex(0);
                        }}
                        className={css.searchField}
                        placeholder={t('search')}
                    />
                    {isEmpty(stores) && hasNoRoles && <div className={css.notAssigned}>{t('navigation.notAssigned')}</div>}
                    {!query && pinnedRoutes.length > 0 && (
                        <NavigationGroup
                            routes={pinnedRoutes}
                            groupName="navigation.pinned"
                            hideNavigation={hideNavigation}
                            disableCollapse
                        />
                    )}
                    {map(groups, (routes, groupName) => (
                        <NavigationGroup
                            key={`navgroup-${groupName}`}
                            routes={routes}
                            groupName={groupName}
                            hideNavigation={hideNavigation}
                            disableCollapse={!!query}
                            highlightItem={query.length > 0 ? isHighlighted : undefined}
                        />
                    ))}

                    <div className={css.spacer} />

                    <Button variant="text" data-testid="navigationItem-version" className={css.version} onClick={showVersions}>
                        {t('navigation.version', { version })}
                    </Button>
                </div>
            </List>
        </ClickOutside>
    );
}
